import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, X, RotateCcw } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner, Button, SectionLabel } from "../components/Primitives";
import { cn } from "../lib/utils";

export default function QuizDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/quizzes/${slug}`).then((r) => setQuiz(r.data)).catch(() => setQuiz(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-[60vh]"><Spinner /></div>;
  if (!quiz) return <div className="mx-auto max-w-7xl px-6 py-32 text-center text-muted">Quiz not found.</div>;

  const q = quiz.questions[idx];
  const answered = selected !== null;
  const score = answers.filter(Boolean).length;

  const choose = (i) => {
    if (answered) return;
    setSelected(i);
    setAnswers((a) => [...a, i === q.correct_index]);
  };

  const next = () => {
    if (idx + 1 < quiz.questions.length) {
      setIdx(idx + 1);
      setSelected(null);
    } else {
      setDone(true);
      if (user) {
        const finalScore = answers.filter(Boolean).length;
        api.post("/progress", {
          item_type: "quiz",
          item_id: quiz.slug,
          title: quiz.title,
          status: "completed",
          score: finalScore,
          total: quiz.questions.length,
        }).catch(() => {});
      }
    }
  };

  const restart = () => {
    setIdx(0); setSelected(null); setAnswers([]); setDone(false);
  };

  return (
    <div data-testid="quiz-detail-page" className="mx-auto max-w-3xl px-6 py-16 md:px-12">
      <Link to="/quizzes" className="mb-6 inline-flex items-center gap-2 text-sm text-gold hover:underline" data-testid="back-to-quizzes">
        <ArrowLeft className="h-4 w-4" /> All Quizzes
      </Link>

      {!done ? (
        <>
          <SectionLabel>{quiz.title}</SectionLabel>
          <div className="mb-6 flex items-center justify-between">
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-muted">
              Question {idx + 1} / {quiz.questions.length}
            </span>
            <div className="h-1 w-40 bg-edge">
              <div className="h-1 bg-gold transition-all" style={{ width: `${((idx + (answered ? 1 : 0)) / quiz.questions.length) * 100}%` }} />
            </div>
          </div>

          <h2 className="font-serif text-3xl text-bone" data-testid="quiz-question">{q.question}</h2>

          <div className="mt-8 space-y-3">
            {q.options.map((opt, i) => {
              const isCorrect = i === q.correct_index;
              const isChosen = i === selected;
              return (
                <button
                  key={i}
                  onClick={() => choose(i)}
                  data-testid={`quiz-option-${i}`}
                  className={cn(
                    "flex w-full items-center justify-between border px-5 py-4 text-left transition-colors",
                    !answered && "border-edge text-bone hover:border-gold/60",
                    answered && isCorrect && "border-[#3a7a3d] bg-forest/20 text-bone",
                    answered && isChosen && !isCorrect && "border-blood bg-blood/15 text-bone",
                    answered && !isChosen && !isCorrect && "border-edge text-muted"
                  )}
                >
                  <span>{opt}</span>
                  {answered && isCorrect && <Check className="h-5 w-5 text-[#7bbd7e]" />}
                  {answered && isChosen && !isCorrect && <X className="h-5 w-5 text-[#e07a72]" />}
                </button>
              );
            })}
          </div>

          {answered && (
            <div className="mt-6 border-l-2 border-gold bg-ash p-5" data-testid="quiz-explanation">
              <h4 className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold">Explanation</h4>
              <p className="mt-1 text-sm leading-relaxed text-[#cfc9bd]">{q.explanation}</p>
              <Button variant="gold" onClick={next} className="mt-5" data-testid="quiz-next-btn">
                {idx + 1 < quiz.questions.length ? "Next Question" : "See Results"}
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center" data-testid="quiz-results">
          <SectionLabel>Complete</SectionLabel>
          <h2 className="font-serif text-5xl text-bone">
            {score} <span className="text-muted">/ {quiz.questions.length}</span>
          </h2>
          <p className="mt-4 text-muted">
            {score === quiz.questions.length
              ? "Outstanding — a scholar's score."
              : score >= quiz.questions.length / 2
              ? "Well done. Revisit the lessons to go deeper."
              : "Keep learning — explore the kingdoms and lessons."}
          </p>
          {!user && <p className="mt-3 text-sm text-gold">Sign in to save your quiz progress.</p>}
          <div className="mt-8 flex justify-center gap-4">
            <Button variant="outline" onClick={restart} data-testid="quiz-restart-btn">
              <RotateCcw className="h-4 w-4" /> Retake
            </Button>
            <Link to="/quizzes"><Button variant="gold">More Quizzes</Button></Link>
          </div>
        </div>
      )}
    </div>
  );
}
